import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Receipt } from 'lucide-react'
import { executionsApi } from '../api/client'
import { useWebSocketEvent } from '../hooks/useWebSocket'
import type { WebSocketHookReturn } from '../hooks/useWebSocket'
import { useExpandablePanel } from '../hooks/useExpandablePanel'
import { ExpandToggle } from './ExpandToggle'
import './RecentExecutions.css'

interface RecentExecutionsProps {
  ws: WebSocketHookReturn
}

const fmtPrice = (v: number) => `$${(Number.isFinite(v) ? v : 0).toFixed(2)}`
const fmtTime = (iso: string) => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString([], { hour12: false })
}

export function RecentExecutions({ ws }: RecentExecutionsProps) {
  const queryClient = useQueryClient()

  const { data: fills, isLoading, isError } = useQuery({
    queryKey: ['recent-executions'],
    queryFn: () => executionsApi.getRecent(25),
    refetchInterval: 10000,
  })

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['recent-executions'] })
  }, [queryClient])
  useWebSocketEvent(ws, 'position_update', invalidate)

  const count = fills?.length ?? 0
  const { expanded, toggle, scrollClassName } = useExpandablePanel('recent-executions')

  return (
    <div className="card recent-executions-card">
      <div className="card-header">
        <h3 className="card-title">
          <Receipt size={20} className="icon" />
          Recent Executions
        </h3>
        <div className="card-header-right">
          {count > 0 && <span className="card-badge">{count}</span>}
          <ExpandToggle expanded={expanded} onToggle={toggle} />
        </div>
      </div>

      {isLoading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : isError ? (
        <div className="empty-state">
          <div className="empty-text">Failed to load executions</div>
        </div>
      ) : count === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><Receipt size={32} aria-hidden="true" /></div>
          <div className="empty-text">No executions yet</div>
        </div>
      ) : (
        <ul className={`executions-list ${scrollClassName}`}>
          {fills?.map((f, i) => (
            <li className="execution-row" key={`${f.symbol}-${f.executed_at}-${i}`}>
              <span className={`exec-side ${f.side}`}>{f.side.toUpperCase()}</span>
              <span className="exec-symbol">{f.symbol}</span>
              <span className="exec-qty mono">×{f.quantity}</span>
              <span className="exec-price mono">{fmtPrice(f.price)}</span>
              <span className="exec-time mono">{fmtTime(f.executed_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
